import { EmotionScore } from "@/api/studio/insights/types/emotion.type";
import { MemoryScore } from "@/api/studio/insights/types/memory.type";
import { ReactionScore } from "@/api/studio/insights/types/metrics.type";
import { Indicator } from "./indicator";

interface MemoryEmotionIndicatorProps {
  score?: EmotionScore | MemoryScore | ReactionScore | null;
  type: "emotion" | "memory" | "reaction";
  size?: "small" | "medium" | "large";
  textSize?: "xs" | "sm" | "base" | "lg";
}

/* Score label mappings */
export const emotionLabels: Record<EmotionScore, string> = {
  "-3": "Very negative",
  "-2": "Negative",
  "-1": "Mildly negative",
  0: "Neutral",
  1: "Mildly positive",
  2: "Positive",
  3: "Very positive",
};

const memoryLabels: Record<MemoryScore, string> = {
  1: "Very weak",
  2: "Weak",
  3: "Moderate",
  4: "Strong",
  5: "Very strong",
};

const reactionLabels: Record<ReactionScore, string> = {
  1: "Negative",
  2: "Neutral",
  3: "Mixed",
  4: "Positive",
};

/* Score color mappings */
export const getEmotionColor = (score: EmotionScore): string => {
  switch (score) {
    case 3:
      return "#198754"; // Very positive
    case 2:
      return "#80B918"; // Positive
    case 1:
      return "#C8C000"; // Mildly positive
    case 0:
      return "#8C8C90"; // Neutral
    case -1:
      return "#CE9321"; // Mildly negative
    case -2:
      return "#DC7135"; // Negative
    case -3:
      return "#DC3545"; // Very negative
    default:
      return "#8C8C90"; // Fallback
  }
};

const getMemoryColor = (score: MemoryScore): string => {
  switch (score) {
    case 5:
      return "#198754"; // Very strong
    case 4:
      return "#80B918"; // Strong
    case 3:
      return "#C8C000"; // Moderate
    case 2:
      return "#DC7135"; // Weak
    case 1:
      return "#DC3545"; // Very weak
    default:
      return "#8C8C90"; // Fallback
  }
};

const getReactionColor = (score: ReactionScore): string => {
  switch (score) {
    case 4:
      return "#80B918"; // Positive
    case 3:
      return "#8C8C90"; // Mixed
    case 2:
      return "#8C8C90"; // Neutral
    case 1:
      return "#DC3545"; // Negative
    default:
      return "#8C8C90"; // Fallback
  }
};

/*
 * MemoryEmotionIndicator component
 *
 * Displays a traffic light style indicator with a label for emotion, memory, and reaction scores.
 */
export const MemoryEmotionIndicator = ({ score, type, size = "medium", textSize = "sm" }: MemoryEmotionIndicatorProps) => {
  /* Consts */
  const label = type === "emotion" ? emotionLabels[score as EmotionScore] : type === "memory" ? memoryLabels[score as MemoryScore] : reactionLabels[score as ReactionScore];

  const color = type === "emotion" ? getEmotionColor(score as EmotionScore) : type === "memory" ? getMemoryColor(score as MemoryScore) : getReactionColor(score as ReactionScore);

  /* If a score isn't returned then show "None" */
  if (score === null || score === undefined) {
    return (
      <div className="flex items-center gap-2">
        <Indicator color="#8C8C90" size={size} />
        <span className={`text-${textSize} ${textSize === "xs" ? "font-medium" : "font-semibold"} whitespace-nowrap`}>None</span>
      </div>
    );
  }

  /* Render */
  return (
    <div className="flex items-center gap-2">
      <Indicator color={color} size={size} />
      <span className={`text-${textSize} ${textSize === "xs" ? "font-medium" : "font-semibold"} whitespace-nowrap`}>{label}</span>
    </div>
  );
};
